import React, { useState } from 'react';
import { View, Text, Pressable, StyleSheet, Platform, Modal, Image, Alert, ActivityIndicator } from 'react-native';
import WebScrollView from '../../components/WebScrollView';
import { MapPin, X, Plus, Loader2, Trash2, Settings } from '../../lib/icons';
import { supabase } from '../../lib/supabase';
import { useNavigate } from 'react-router-dom';
import { shadows } from '../../lib/styles';

interface Home {
  id: string;
  address: string;
  city: string;
  state: string;
  zip_code: string;
  square_footage: number | null;
  year_built: number | null;
  cover_photo_url?: string;
  lot_size?: number;
  bedrooms?: number;
  bathrooms?: number;
}

interface HomesListProps {
  homes: Home[];
  onClose: () => void;
  onAddHome: () => void;
  onHomeUpdated: () => void;
  isOpen: boolean;
  allowAddHome?: boolean;
}

const HomesList: React.FC<HomesListProps> = ({ homes, onClose, onAddHome, onHomeUpdated, isOpen, allowAddHome = true }) => {
  const navigate = useNavigate();
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState('');

  const handleDelete = async (homeId: string) => {
    Alert.alert(
      'Delete Home',
      'Are you sure you want to delete this home? This action cannot be undone.',
      [
        { text: 'Cancel', style: 'cancel' },
        {
          text: 'Delete',
          style: 'destructive',
          onPress: async () => {
            setDeleting(true);
            setError('');

            try {
              const { error } = await supabase
                .from('homes')
                .delete()
                .eq('id', homeId);

              if (error) throw error;
              onHomeUpdated();
            } catch (error: any) {
              setError(error.message);
            } finally {
              setDeleting(false);
            }
          }
        }
      ]
    );
  };

  const handleConfigureSystems = (homeId: string) => {
    onClose();
    navigate('/maintenance-plan');
  };

  return (
    <Modal
      visible={isOpen}
      transparent
      animationType="fade"
      onRequestClose={onClose}
    >
      <View style={styles.overlay}>
        <Pressable style={styles.backdrop} onPress={onClose} />
        <View style={styles.modal}>
          <View style={styles.header}>
            <View>
              <Text style={styles.title}>Your Homes</Text>
              <Text style={styles.subtitle}>
                Manage all your properties in one place
              </Text>
            </View>
            <Pressable onPress={onClose} style={styles.closeButton}>
              <X size={20} color="#9ca3af" />
            </Pressable>
          </View>

          {allowAddHome && (
            <View style={styles.addButtonContainer}>
              <Pressable
                onPress={onAddHome}
                style={styles.addButton}
              >
                <Plus size={16} color="#ffffff" />
                <Text style={styles.addButtonText}>Add Home</Text>
              </Pressable>
            </View>
          )}

          {error && (
            <View style={styles.errorContainer}>
              <Text style={styles.errorText}>{error}</Text>
            </View>
          )}

          <WebScrollView style={styles.list} contentContainerStyle={styles.listContent}>
            {homes.length === 0 ? (
              <View style={styles.emptyState}>
                <MapPin size={48} color="#9ca3af" />
                <Text style={styles.emptyTitle}>No Homes Added</Text>
                <Text style={styles.emptyText}>
                  Add your first home to start managing your property
                </Text>
                {allowAddHome && (
                  <Pressable
                    onPress={onAddHome}
                    style={styles.emptyAddButton}
                  >
                    <Plus size={16} color="#ffffff" />
                    <Text style={styles.emptyAddButtonText}>Add Your First Home</Text>
                  </Pressable>
                )}
              </View>
            ) : (
              homes.map((home) => (
                <View key={home.id} style={styles.homeCard}>
                  <View style={styles.homeContent}>
                    <View style={styles.homeInfo}>
                      {home.cover_photo_url ? (
                        <Image
                          source={{ uri: home.cover_photo_url }}
                          style={styles.homeImage}
                        />
                      ) : (
                        <View style={styles.homeImagePlaceholder}>
                          <MapPin size={24} color="#9ca3af" />
                        </View>
                      )}
                      <View style={styles.homeDetails}>
                        <View>
                          <Text style={styles.homeAddress}>{home.address}</Text>
                          <Text style={styles.homeLocation}>
                            {home.city}, {home.state} {home.zip_code}
                          </Text>
                        </View>
                        {(home.square_footage || home.year_built || home.bedrooms || home.bathrooms) && (
                          <View style={styles.homeMeta}>
                            {home.square_footage && (
                              <Text style={styles.metaText}>
                                {home.square_footage.toLocaleString()} sq ft
                              </Text>
                            )}
                            {home.year_built && (
                              <Text style={styles.metaText}>Built {home.year_built}</Text>
                            )}
                            {home.bedrooms && (
                              <Text style={styles.metaText}>
                                {home.bedrooms} bed{home.bedrooms !== 1 ? 's' : ''}
                              </Text>
                            )}
                            {home.bathrooms && (
                              <Text style={styles.metaText}>
                                {home.bathrooms} bath{home.bathrooms !== 1 ? 's' : ''}
                              </Text>
                            )}
                          </View>
                        )}
                        <Pressable
                          onPress={() => handleConfigureSystems(home.id)}
                          style={styles.configureButton}
                        >
                          <Settings size={16} color="#2563eb" />
                          <Text style={styles.configureText}>Configure Systems</Text>
                        </Pressable>
                      </View>
                    </View>
                    <Pressable
                      onPress={() => handleDelete(home.id)}
                      disabled={deleting}
                      style={styles.deleteButton}
                    >
                      {deleting ? (
                        <ActivityIndicator size="small" color="#dc2626" />
                      ) : (
                        <Trash2 size={20} color="#dc2626" />
                      )}
                    </Pressable>
                  </View>
                </View>
              ))
            )}
          </WebScrollView>
        </View>
      </View>
    </Modal>
  );
};

const styles = StyleSheet.create({
  overlay: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    padding: 16,
  },
  backdrop: {
    ...StyleSheet.absoluteFillObject,
  },
  modal: {
    backgroundColor: '#ffffff',
    borderRadius: 12,
    width: '100%',
    maxWidth: 768,
    maxHeight: '80%',
    padding: 24,
    ...(Platform.OS === 'web' && {
      ...shadows.lg,
    }),
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 16,
  },
  title: {
    fontSize: 20,
    fontWeight: '600',
    color: '#111827',
  },
  subtitle: {
    fontSize: 14,
    color: '#4b5563',
    marginTop: 4,
  },
  closeButton: {
    padding: 4,
  },
  addButtonContainer: {
    marginBottom: 16,
  },
  addButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    paddingHorizontal: 16,
    paddingVertical: 8,
    backgroundColor: '#000000',
    borderRadius: 8,
    width: '100%',
  },
  addButtonText: {
    color: '#ffffff',
    fontSize: 16,
    fontWeight: '500',
  },
  errorContainer: {
    marginBottom: 16,
    padding: 12,
    backgroundColor: '#fef2f2',
    borderRadius: 8,
  },
  errorText: {
    color: '#dc2626',
    fontSize: 14,
  },
  list: {
    maxHeight: '60%',
  },
  listContent: {
    gap: 16,
  },
  emptyState: {
    alignItems: 'center',
    paddingVertical: 48,
  },
  emptyTitle: {
    fontSize: 18,
    fontWeight: '500',
    color: '#111827',
    marginTop: 16,
    marginBottom: 8,
  },
  emptyText: {
    fontSize: 14,
    color: '#4b5563',
    textAlign: 'center',
    marginBottom: 16,
  },
  emptyAddButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    paddingHorizontal: 16,
    paddingVertical: 8,
    backgroundColor: '#000000',
    borderRadius: 8,
  },
  emptyAddButtonText: {
    color: '#ffffff',
    fontSize: 16,
    fontWeight: '500',
  },
  homeCard: {
    padding: 16,
    backgroundColor: '#f9fafb',
    borderRadius: 12,
  },
  homeContent: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
  },
  homeInfo: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 12,
    flex: 1,
  },
  homeImage: {
    width: 64,
    height: 64,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#e5e7eb',
  },
  homeImagePlaceholder: {
    width: 64,
    height: 64,
    backgroundColor: '#e5e7eb',
    borderRadius: 8,
    justifyContent: 'center',
    alignItems: 'center',
  },
  homeDetails: {
    flex: 1,
    gap: 8,
  },
  homeAddress: {
    fontSize: 16,
    fontWeight: '500',
    color: '#111827',
  },
  homeLocation: {
    fontSize: 14,
    color: '#4b5563',
    marginTop: 2,
  },
  homeMeta: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 12,
  },
  metaText: {
    fontSize: 14,
    color: '#6b7280',
  },
  configureButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    paddingHorizontal: 12,
    paddingVertical: 6,
    backgroundColor: '#eff6ff',
    borderRadius: 8,
    alignSelf: 'flex-start',
  },
  configureText: {
    fontSize: 14,
    fontWeight: '500',
    color: '#2563eb',
  },
  deleteButton: {
    padding: 8,
    borderRadius: 8,
  },
});

export default HomesList;
